import React, { useState, useEffect } from 'react';
import { Card, Col, Container, Row, Table, Modal, Button, Form, Dropdown } from 'react-bootstrap';
import BreadCrumb from 'Common/BreadCrumb';
import Swal from 'sweetalert2'; // Import SweetAlert2
import axios from 'axios'; // Import axios

const Educations = () => {
  document.title = 'Education | CITY SERVICE';

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    nama: '',
    jurusan: [''], // Array to store multiple majors
  }); // Updated form data state
  const [editMode, setEditMode] = useState(false); // State for edit mode
  const [editId, setEditId] = useState<number | null>(null); // State for edit ID
  const [isLoading, setIsLoading] = useState(false); // Loading state
  const [tableData, setTableData] = useState<{ id: number; nama: string; jurusan: string[]; created_at: string; updated_at: string }[]>([]); // Ensure default value is an empty array

  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setEditId(null);
    setFormData({ nama: '', jurusan: [''] });
  };

  const handleEditModal = (education: { id: number; nama: string; jurusan: string[] }) => {
    setEditMode(true);
    setEditId(education.id);
    setFormData({ nama: education.nama, jurusan: education.jurusan || [''] });
    setShowModal(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
  };

  const handleJurusanChange = (index: number, value: string) => {
    const newJurusan = [...formData.jurusan];
    newJurusan[index] = value;
    setFormData({ ...formData, jurusan: newJurusan });
  };

  const addJurusan = () => {
    setFormData({ ...formData, jurusan: [...formData.jurusan, ''] });
  };

  const removeJurusan = (index: number) => {
    if (formData.jurusan.length > 1) {
      const newJurusan = formData.jurusan.filter((_, i) => i !== index);
      setFormData({ ...formData, jurusan: newJurusan });
    }
  };

  const handleFormSubmit = async () => {
    setIsLoading(true); // Start loading
    Swal.fire({
      title: editMode ? 'Updating...' : 'Saving...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken'); // Retrieve token from localStorage
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      // Filter out empty jurusan entries
      const filteredJurusan = formData.jurusan.filter(j => j.trim() !== '');

      let response;
      if (editMode && editId) {
        // Update existing education
        response = await axios.patch(
          `/master-educations/${editId}`,
          {
            nama: formData.nama,
            jurusan: filteredJurusan,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`, // Add token to Authorization header
            },
          }
        );
        Swal.fire('Success', `Education "${response.data.nama}" updated successfully!`, 'success');
      } else {
        // Create new education
        response = await axios.post(
          '/master-educations',
          {
            nama: formData.nama,
            jurusan: filteredJurusan,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`, // Add token to Authorization header
            },
          }
        );
        Swal.fire('Success', `Education "${response.data.nama}" added successfully!`, 'success');
      }

      setShowModal(false);
      setEditMode(false);
      setEditId(null);
      setFormData({ nama: '', jurusan: [''] }); // Reset form
      fetchEducations(); // Refetch educations to update the table
    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || (error instanceof Error && error.message) || 'An unknown error occurred',
        'error'
      );
    } finally {
      setIsLoading(false); // End loading
    }
  };

  const fetchEducations = async () => {
    setIsLoading(true); // Start loading
    Swal.fire({
      title: 'Loading...',
      html: 'Please wait while we fetch the data.',
      timerProgressBar: true,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken'); // Retrieve token from localStorage
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      const response = await axios.get('/master-educations', {
        headers: {
          Authorization: `Bearer ${token}`, // Add token to Authorization header
        },
      });

      setTableData(response.data.data || []); // Update table data with API response
      Swal.fire({
        icon: 'success',
        title: 'Data Loaded',
        showConfirmButton: false,
        timer: 1000, // Smooth transition after loading
      });
    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || (error instanceof Error && error.message) || 'Failed to fetch educations',
        'error'
      );
    } finally {
      setIsLoading(false); // End loading
    }
  };

  const handleDelete = async (id: number) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You won\'t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsLoading(true); // Start loading
        try {
          const token = localStorage.getItem('authToken'); // Retrieve token from localStorage
          if (!token) {
            throw new Error('Authentication token is missing. Please log in again.');
          }

          await axios.delete(`/master-educations/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`, // Add token to Authorization header
            },
          });

          Swal.fire('Deleted!', 'The education has been deleted.', 'success');
          setTableData((prevData) => prevData.filter((item) => item.id !== id)); // Remove deleted item from table
        } catch (error) {
          Swal.fire(
            'Error',
            (axios.isAxiosError(error) && error.response?.data?.message) || (error instanceof Error && error.message) || 'Failed to delete education',
            'error'
          );
        } finally {
          setIsLoading(false); // End loading
        }
      }
    });
  };

  useEffect(() => {
    fetchEducations(); // Fetch educations on component mount
  }, []); // Fetch educations on component mount

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb pageTitle="Education" title="Education" />

          <Row>
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0"></h5>
                    <div className="ms-auto"> {/* Add ms-auto to push the button to the right */}
                      <button
                        className="btn btn-dark d-flex align-items-center gap-1"
                        onClick={handleShowModal}
                      >
                        <i className="ri-add-line"></i>
                        <span>Create</span>
                      </button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Pendidikan</th>
                        <th>Jurusan</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(tableData) && tableData.length > 0 ? ( // Ensure tableData is an array and not empty
                        tableData.map((education, index) => (
                          <tr key={education.id}>
                            <td>{index + 1}</td>
                            <td>{education.nama}</td>
                            <td>
                              {education.jurusan && education.jurusan.length > 0 
                                ? (
                                  <div className="d-flex flex-wrap gap-1">
                                    {education.jurusan.map((jurusan, jurusanIndex) => (
                                      <span 
                                        key={jurusanIndex} 
                                        className="badge d-flex align-items-center gap-1"
                                        style={{ 
                                          fontSize: '0.75rem',
                                          backgroundColor: 'transparent',
                                          border: '1px solid #0d6efd',
                                          color: '#0d6efd'
                                        }}
                                      >
                                        <i className="ri-price-tag-3-line" style={{ fontSize: '0.7rem' }}></i>
                                        {jurusan}
                                      </span>
                                    ))}
                                  </div>
                                )
                                : (
                                  <span className="text-muted">
                                    <i className="ri-subtract-line me-1"></i>
                                    No majors
                                  </span>
                                )
                              }
                            </td>
                            <td>
                              <Dropdown>
                                <Dropdown.Toggle variant="dark" id={`action-dropdown-${education.id}`} className="btn-sm">
                                  <i className="ri-more-2-fill"></i> {/* Add an icon for the dropdown toggle */}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item onClick={() => handleEditModal(education)}>
                                    <i className="ri-edit-line me-2"></i> Edit {/* Add an edit icon */}
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => handleDelete(education.id)}>
                                    <i className="ri-delete-bin-line me-2"></i> Delete {/* Add a delete icon */}
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="text-center">
                            No educations available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Modal for Adding Education */}
        <Modal show={showModal} onHide={handleCloseModal} backdrop="static" centered>
          <Modal.Header closeButton>
            <Modal.Title>{editMode ? 'Edit Education' : 'Add Education'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="nama">Pendidikan</Form.Label>
                <Form.Control
                  type="text"
                  id="nama"
                  placeholder="Enter the education level (e.g., SD, SMP, SMA, D3, S1, S2, S3)"
                  value={formData.nama}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  isInvalid={!formData.nama.trim()} // Validation feedback
                />
                <Form.Control.Feedback type="invalid">
                  Pendidikan is required.
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="mb-2">Jurusan</Form.Label>
                
                {formData.jurusan.map((jurusan, index) => (
                  <div key={index} className="d-flex mb-2">
                    <Form.Control
                      type="text"
                      placeholder={`Enter jurusan ${index + 1} (e.g., Teknik Informatika, Akuntansi, dll)`}
                      value={jurusan}
                      onChange={(e) => handleJurusanChange(index, e.target.value)}
                      disabled={isLoading}
                      className="me-2"
                    />
                    {formData.jurusan.length > 1 && (
                      <Button 
                        variant="outline-danger" 
                        size="sm" 
                        onClick={() => removeJurusan(index)}
                        disabled={isLoading}
                      >
                        <i className="ri-delete-bin-line"></i>
                      </Button>
                    )}
                  </div>
                ))}
                
                <div className="d-flex justify-content-between align-items-center mt-2 flex-nowrap">
                  <Form.Text className="text-muted me-2" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    Add multiple majors for this education level.
                  </Form.Text>
                  <Button 
                    variant="outline-dark" 
                    size="sm" 
                    onClick={addJurusan}
                    disabled={isLoading}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    <i className="ri-add-line me-1"></i>
                    Add Jurusan
                  </Button>
                </div>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="dark" onClick={handleCloseModal} disabled={isLoading}>
              Close
            </Button>
            <Button variant="dark" onClick={handleFormSubmit} disabled={isLoading}>
              {isLoading ? (editMode ? 'Updating...' : 'Saving...') : (editMode ? 'Update' : 'Save')}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default Educations;